/**
 * Settings → Access Control
 * BUILD 1: Toggle strict_project_scoping on/off with pre-flight check.
 */
import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { ShieldAlert, AlertTriangle, CheckCircle2, RefreshCw, Users } from 'lucide-react';

export default function AccessControlSettings() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await apiGet('/settings/access-control')); }
    catch { toast.error('Could not load settings'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!data) return;
    const newStrict = !data.strict_project_scoping;
    if (newStrict && data.unassigned_employee_count > 0 && !confirmed) {
      toast.error(`Confirm first — ${data.unassigned_employee_count} employee(s) would lose access.`);
      return;
    }
    setSaving(true);
    try {
      const res = await apiPut('/settings/access-control', {
        strict_project_scoping: newStrict,
        confirmed,
      });
      if (res?.ok === false) {
        toast.error(res.warning || 'Cannot enable without confirmation');
        setSaving(false);
        return;
      }
      toast.success(newStrict ? 'Strict project scoping ENABLED' : 'Strict project scoping disabled (permissive mode)');
      setConfirmed(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-400">Loading…</div>;
  if (!data) return null;

  const strict = data.strict_project_scoping;
  const unassigned = data.unassigned_employee_count || 0;

  return (
    <div className="p-6 max-w-2xl space-y-6" data-testid="access-control-settings-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2">
          <ShieldAlert size={20} className="text-[#3b4872]" /> Project Scoping
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Controls whether non-exec employees are restricted to their assigned projects only.
        </p>
      </div>

      {/* Status card */}
      <div className={`rounded-xl border-2 p-5 ${strict ? 'border-emerald-400 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
        <div className="flex items-center gap-3 mb-2">
          {strict
            ? <CheckCircle2 size={20} className="text-emerald-600" />
            : <AlertTriangle size={20} className="text-amber-600" />
          }
          <div className="font-display font-bold text-base">
            {strict ? 'STRICT MODE — Employees see only assigned projects' : 'PERMISSIVE MODE — All employees see all projects (rollout phase)'}
          </div>
        </div>
        <p className="text-sm text-slate-600">
          {strict
            ? 'Any employee without a project assignment will see an empty project list. Enable with caution — make sure all employees are assigned first.'
            : 'During rollout, non-exec employees with no project assignment still see all projects (old behaviour). Flip to strict once HR has assigned projects to everyone.'}
        </p>
      </div>

      {/* Unassigned count */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
        <Users size={16} className="text-slate-500" />
        <div className="flex-1">
          <div className="font-display font-bold text-sm">{unassigned} employee(s) have no project assigned</div>
          <div className="text-xs text-slate-500">
            {unassigned === 0
              ? 'All employees are assigned to at least one project. Safe to enable strict mode.'
              : `These employees would lose all project access if you enable strict mode now. Assign them in Employee Master first.`}
          </div>
        </div>
        <a href="/app/hr/employees?filter=no_project" className="text-xs text-[#3b4872] font-bold hover:underline whitespace-nowrap">
          View unassigned →
        </a>
      </div>

      {/* Confirmation for enabling strict */}
      {!strict && unassigned > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
          <p className="text-sm text-rose-700 font-bold mb-2">
            ⚠ Enabling strict mode will lock out {unassigned} employee(s) immediately.
          </p>
          <label className="flex items-center gap-2 text-sm cursor-pointer text-rose-800">
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} data-testid="strict-confirm-checkbox" />
            I understand — enable strict project scoping anyway
          </label>
        </div>
      )}

      {/* Toggle button */}
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          disabled={saving || (!strict && unassigned > 0 && !confirmed)}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-display font-bold text-sm transition-colors disabled:opacity-40 ${
            strict
              ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300'
              : 'bg-[#3b4872] hover:bg-[#2d3856] text-white'
          }`}
          data-testid="strict-mode-toggle-btn"
        >
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
          {strict ? 'Switch to Permissive Mode' : 'Enable Strict Project Scoping'}
        </button>
        <button onClick={load} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* How it works */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 space-y-2">
        <p className="font-bold text-slate-700">How project scoping works (BUILD 1):</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Execs (MD/CEO/CFO/CMD/Super Admin) always see all projects regardless of this setting.</li>
          <li>Non-exec employees see projects via: <strong>Employee → Additional Sites</strong>, their primary project, project team membership, or PM assignment.</li>
          <li><strong>Permissive (default):</strong> Employees with no assignments see all projects — old behaviour preserved during rollout.</li>
          <li><strong>Strict:</strong> Employees with no assignments see nothing — requires all employees to be assigned first.</li>
        </ul>
      </div>
    </div>
  );
}
